import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from "rxjs/operators";
import { ErrorResponse } from '../models/ErrorResponse';
import { NgEventBus } from 'ng-event-bus';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { LocalService } from '../services/local.service';
import { AuthService } from '../services/auth.service';
import { environment } from '../../environments/environment';

// Transient 401 codes from the Api AuthGuard: the session itself is not known to be invalid.
const NON_TERMINAL_401_CODES = ['unavailable', 'legacy'];

@Injectable()
export class ErrorCatchingInterceptor implements HttpInterceptor {
  private isRedirecting = false;

  constructor(
    private _eventBus: NgEventBus,
    private _translate: TranslateService,
    private _router: Router,
    private _localService: LocalService,
    private _authService: AuthService,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.handle(request, next, true);
  }

  private handle(request: HttpRequest<unknown>, next: HttpHandler, allowRetry: boolean): Observable<HttpEvent<unknown>> {
    return next.handle(request)
      .pipe(
        map((res: HttpResponse<unknown>) => {
          return res
        }),
        catchError((errorResponse: HttpErrorResponse) => {
          const { error }: ErrorResponse = errorResponse;

          if (errorResponse.status === 401) {
            const code = error && (error as any).code;
            if (NON_TERMINAL_401_CODES.includes(code)) {
              return throwError(error);
            }

            // A parallel response may have renewed the token after this request was sent.
            const renewedToken = this.getRenewedToken(request);
            if (allowRetry && renewedToken) {
              const retried = request.clone({ setHeaders: { Authorization: `Bearer ${renewedToken}` } });
              return this.handle(retried, next, false);
            }

            if (!this.isRedirecting) {
              this.isRedirecting = true;
              this._localService.clearToken();
              sessionStorage.clear();
              this._router.navigate(['/login'])
                .then(() => { this.isRedirecting = false; })
                .catch(() => { this.isRedirecting = false; });
            }
          }

          if (errorResponse.url.match("/marketing/start")) {
            if (error.message) {
              this._eventBus.cast('notification: errorInData', {
                type: 'error',
                message: `${this._translate.instant(`admin.campaign.${error.message}`)}`,
                id: `generateBackup`,
                loading: false,
              });
            }
          }

          return throwError(error)
        })
      )
  }

  private getRenewedToken(request: HttpRequest<unknown>): string | null {
    if (!request.url.startsWith(environment.baseServerURL)) {
      return null;
    }
    const current = this._authService.currentUserValue;
    const token = current && current.token;
    const sentHeader = request.headers.get('Authorization');
    if (!token || !sentHeader || sentHeader === `Bearer ${token}`) {
      return null;
    }
    return token;
  }
}
